<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import SeatsTable from '@/components/Admin/SeatsTable.vue'
import type { Study } from '@pocketprep/types'
import { instructorModule } from '@/store/instructor/module'
import { organizationModule } from '@/store/organization/module'
import { groupModule } from '@/store/group/module'
import { examModule } from '@/store/exam/module'
import { userModule } from '@/store/user/module'
import { toastModule } from '@/store/toast/module'

interface IAccountTypeOption {
    label: 'Admin' | 'Instructor'
    helperText: string
    value: 'admin' | 'instructor'
}

const {
    SidePanel,
    Button: PocketButton,
    Radio: PocketRadio,
    Input: PocketInput,
    Textarea: PocketTextarea,
    Icon,
    ModalContainer,
} = UIKit

const props = defineProps<{
    instructorId: string
}>()

const emit = defineEmits<{
    close: [value?: boolean]
}>()

const isLoading = ref(false)
const showCancelInstructorInviteModal = ref(false)
const openModalIds = ref<string[]>([])
const activeTabName = ref('Account Type')
const accountType = ref<IAccountTypeOption>({
    label: 'Instructor',
    helperText: 'placeholder',
    value: 'instructor',
})
const email = ref('')
const error = ref('')
const customMessage = ref('')
const orgExamIds = ref<string[]>([])
const tabs = [
    'Account Type',
    'Add Email',
]
const accountTypeOptions: IAccountTypeOption[] = [
    {
        label: 'Admin',
        helperText: 'Can view all student\'s stats and edit all students in the organization.<br>Can add students '
            + 'to any exam.',
        value: 'admin',
    },
    {
        label: 'Instructor',
        helperText: 'Can view their student\'s stats and edit their students.<br>Can add students to their exams.',
        value: 'instructor',
    },
]

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const yourOrgExamIds = computed(() => {
    return examModule.getters.getYourOrgExamIds()
})

const isNextBtnDisabled = computed(() => {
    if (
        activeTabName.value === 'Account Type'
        && accountType.value.value === 'instructor'
        && !orgExamIds.value.length
    ) {
        return true
    }
    return false
})

const disabledExamIds = computed(() => {
    if (currentOrgInstructor.value?.isAdmin) {
        return []
    }
    return Object.values(currentOrgExams.value)
        .filter(e => e && !yourOrgExamIds.value.includes(e.objectId)).map(e => e?.objectId)
})

const emitClose = () => {
    emit('close', true)
}

const cancelInvite = async () => {
    if (props.instructorId) {
        await instructorModule.actions.cancelInstructorInvite({ instructorId: props.instructorId })
        toastModule.actions.displayToast({
            title: `${accountType.value.value === 'admin' ? 'Admin' : 'Instructor'} invite canceled.`,
        })
    }
    emitClose()
}

const changeActiveTab = (tabName: string) => {
    if (isNextBtnDisabled.value) {
        return
    }
    activeTabName.value = tabName
}

const nextTab = () => {
    const currentIndex = tabs.indexOf(activeTabName.value)
    if (currentIndex === tabs.length - 1) {
        return
    }
    activeTabName.value = tabs[currentIndex + 1] as string
}

const previousTab = () => {
    const currentIndex = tabs.indexOf(activeTabName.value)
    if (currentIndex === 0) {
        return
    }
    activeTabName.value = tabs[currentIndex - 1] as string
}

const checkedExamsChanged = (checkedRowIds: string[]) => {
    orgExamIds.value = checkedRowIds
}

const submitAddInstructor = async () => {
    error.value = ''

    if (!currentOrganization.value) {
        throw new Error('submitAddInstructor: No current organization selected.')
    }

    if (!currentOrgExams.value) {
        throw new Error('submitAddInstructor: No current exams found.')
    }

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(email.value)) {
        error.value = 'Invalid email address. Only one instructor can be added at a time.'
        return
    }

    isLoading.value = true
    const resolvedOrgExamIds = accountType.value?.value === 'admin'
        ? Object.values(currentOrgExams.value).map(e => e && e.objectId).filter((e): e is string => !!e)
        : orgExamIds.value

    try {
        if (props.instructorId) {
            await instructorModule.actions.updateInstructor({
                instructorId: props.instructorId,
                email: email.value.toLowerCase(),
                customMessage: customMessage.value,
                orgExamIds: resolvedOrgExamIds,
                accountType: accountType.value.value,
                hasBillingAccess: false,
            })
        } else {
            await instructorModule.actions.addInstructor({
                organizationId: currentOrganization.value.objectId,
                email: email.value.toLowerCase(),
                customMessage: customMessage.value,
                orgExamIds: resolvedOrgExamIds,
                accountType: accountType.value.value,
                hasBillingAccess: false,
            })
        }
        organizationModule.actions.validateOrgState()
        await userModule.actions.trackEvent('Invite_Colleague')
        toastModule.actions.displayToast({
            title: 'Invite sent.',
        })
    } catch (e) {
        alert(e)
    }
    emitClose()
    isLoading.value = false
}

const saveOpenModal = (modalId: string) => {
    openModalIds.value.push(modalId)
}

const removeOpenModal = (modalId: string) => {
    openModalIds.value = openModalIds.value.filter(id => id !== modalId)
}

onMounted(async () => {
    isLoading.value = true
    if (!currentOrgInstructor.value?.isAdmin) {
        accountType.value = accountTypeOptions[1] as IAccountTypeOption
    } else {
        accountType.value = accountTypeOptions[0] as IAccountTypeOption
    }

    await examModule.actions.fetchYourOrgExamIds()

    if (props.instructorId) {
        changeActiveTab('Add Email')

        await Promise.all([
            instructorModule.actions.fetchCurrentOrgInstructors(),
            groupModule.actions.fetchCurrentOrgGroups(),
        ])

        if (!currentOrgInstructors.value) {
            throw new Error('AddInstructor: Unable to fetch current org instructors.')
        }

        const instructorSystemGroups = groupModule.getters.searchCurrentOrgGroups({
            instructorId: props.instructorId,
            type: 'system',
        })

        const inst = currentOrgInstructors.value[props.instructorId]
        const examInstructorIds = instructorSystemGroups.map(group => group.orgExam.objectId)

        email.value = (inst?.user as Study.Class.UserJSON).username
        accountType.value = inst?.isAdmin
            ? accountTypeOptions[0] as IAccountTypeOption
            : accountTypeOptions[1] as IAccountTypeOption
        orgExamIds.value = examInstructorIds
        customMessage.value = inst?.customInviteMessage || ''
    }

    await Promise.all([
        organizationModule.actions.fetchCurrentOrganization(),
        examModule.actions.fetchCurrentOrgExams(),
    ])

    const allOrgExams = Object.values(currentOrgExams.value)
        .filter(orgExam => !disabledExamIds.value.includes(orgExam?.objectId))
    const firstOrgExam = allOrgExams[0]
    if (allOrgExams.length === 1 && firstOrgExam) {
        orgExamIds.value = [ firstOrgExam.objectId ]
    }

    isLoading.value = false
})
</script>

<template>
    <Portal to="modal">
        <ModalContainer
            v-if="showCancelInstructorInviteModal"
            :show-close-button="true"
            :remove-head-border-bottom="true"
            @close="removeOpenModal('cancelModal'); showCancelInstructorInviteModal = false"
            @mounted="saveOpenModal('cancelModal')"
        >
            <template #head>
                <div
                    class="add-instructor__cancel-invite-head"
                >
                    Cancel Invite?
                </div>
            </template>
            <div
                class="add-instructor__cancel-modal"
            >
                <span
                    class="add-instructor__cancel-text"
                >
                    {{
                        accountType.value === 'admin' ? 'Admin' : 'Instructor'
                    }} invite will be canceled and their link to join your organization will no longer work.
                </span>
                <div
                    class="add-instructor__cancel-buttons"
                >
                    <PocketButton
                        class="add-instructor__nevermind-btn"
                        type="tertiary"
                        @click="showCancelInstructorInviteModal = false"
                    >
                        Never Mind
                    </PocketButton>
                    <PocketButton
                        class="add-instructor__cancel-btn"
                        type="primary-red"
                        @click="cancelInvite"
                    >
                        Cancel Invite
                    </PocketButton>
                </div>
            </div>
        </ModalContainer>
    </Portal>
    <Portal to="sidepanel">
        <SidePanel
            :tabs="tabs"
            :active-tab-name="activeTabName"
            :stepped-tabs="true"
            :name="instructorId ? 'Edit Pending Colleague' : 'Add Colleague'"
            class="add-instructor"
            :cancel-button-text="instructorId ? 'Cancel Invite' : 'Cancel'"
            :custom-cancel-button-action="!!instructorId"
            :isTeachSidePanel="true"
            :trapFocus="openModalIds[openModalIds.length - 1] === 'activitySidepanel'"
            @mounted="saveOpenModal('instructorSidepanel')"
            @close="removeOpenModal('instructorSidepanel'); emitClose()"
            @closeButton="showCancelInstructorInviteModal = true"
            @selectTab="changeActiveTab"
        >
            <template v-if="activeTabName === 'Account Type'">
                <label>Account Type</label>
                <p v-if="!currentOrgInstructor || !currentOrgInstructor.isAdmin">
                    Only admin can invite other admin to this organization.
                </p>
                <p v-else>
                    Are you adding an instructor or an admin? (You can change this later.)
                </p>
                <PocketRadio
                    v-model="accountType"
                    class="add-instructor__field"
                    :data="accountTypeOptions"
                    :disabled="!currentOrgInstructor || !currentOrgInstructor.isAdmin"
                />
                <template v-if="accountType.value === accountTypeOptions[1]?.value">
                    <div class="add-instructor__exam-label">
                        What exam(s) does this instructor teach?
                    </div>
                    <SeatsTable
                        v-if="!isLoading"
                        class="add-instructor__seats-table"
                        :show-checkboxes="true"
                        :initial-checked-exam-ids="orgExamIds"
                        theme="condensed"
                        :disabled-exam-ids="disabledExamIds"
                        :disable-seatless-exams="false"
                        @checkedRowsChanged="checkedExamsChanged"
                    />
                </template>
            </template>
            <template v-if="activeTabName === 'Add Email'">
                <label>Add Email</label>
                <p>What's the best email for your colleague?</p>
                <PocketInput
                    v-model="email"
                    label="Colleague's Email"
                    field-type="email"
                    class="add-instructor__text-field"
                />
                <PocketTextarea
                    v-model="customMessage"
                    height="114px"
                    label="Add a personal note to the invite email (optional)"
                    class="add-instructor__text-field"
                />
                <div v-if="error" class="add-instructor__error">
                    <Icon class="add-instructor__error-icon" type="warning" />
                    <span>{{ error }}</span>
                </div>
            </template>

            <template #action>
                <template v-if="activeTabName !== 'Add Email'">
                    <PocketButton
                        type="primary"
                        :disabled="isNextBtnDisabled"
                        @click="nextTab"
                    >
                        Next <Icon type="arrow" class="add-instructor__next-arrow" />
                    </PocketButton>
                </template>
                <template v-else>
                    <PocketButton
                        :disabled="isLoading || !email"
                        type="primary"
                        @click="submitAddInstructor"
                    >
                        {{ instructorId ? 'Resend Invite Email' : 'Add Colleague' }}
                    </PocketButton>
                </template>
            </template>
            <template v-if="activeTabName !== 'Account Type'" #details>
                <PocketButton
                    type="tertiary"
                    class="add-instructor__back"
                    @click="previousTab"
                >
                    <Icon type="arrow" class="add-instructor__back-arrow" />Back
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.add-instructor {
    :deep(*) {
        .uikit-side-panel__tab--stepper {
            margin-bottom: 0;
        }
        .uikit-side-panel__tab--stepper::after {
            bottom: 0;
        }
    }
    &__field {
        margin-bottom: 42px;
        margin-left: -6px;
    }

    &__text-field {
        margin-left: -11px;
        margin-bottom: 20px;
    }

    &__exam-label {
        font-size: 15px;
        line-height: 20px;
        font-weight: 500;
        margin-bottom: 14px;
    }

    &__seats-table {
        margin-left: -6px;
    }

    &__next-arrow {
        margin-left: 6px;
    }

    &__back-arrow {
        margin-right: 10px;
        transform: rotate(180deg);
    }

    &__back {
        padding-left: 0;
    }

    &__error {
        display: flex;
        align-items: center;
        color: #c238f3;
        font-size: 14px;
        line-height: 19px;
        margin-bottom: 26px;
    }

    &__error-icon {
        margin-right: 10px;
    }

    &__cancel-modal {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 374px;
        box-sizing: border-box;
    }

    &__cancel-invite-head {
        margin: 60px auto 25px;
        font-size: 18px;
        color: $brand-black;
        font-weight: 600;
    }

    &__nevermind-btn {
        margin-right: 18px;
        color: $slate;
    }

    &__cancel-buttons {
        display: flex;
        margin: 32px 0 24px;
    }

    &__cancel-text {
        font-size: 15px;
        color: $ash;
        width: 294px;
        text-align: center;
        margin: 20px 0 0;
    }
}
</style>
